import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { authErrorText } from "@/lib/auth-errors";
import { requestPasswordReset } from "@/lib/auth-emails.functions";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLocale } from "@/lib/i18n/locale-context";
import { getPageCopy } from "@/lib/i18n/page-copy";
import { localizedPath } from "@/lib/i18n/slug-map";

export const Route = createFileRoute("/glomt-losenord")({
  head: () => ({
    meta: [
      { title: "Glömt lösenord — Sellavo" },
      {
        name: "description",
        content: "Återställ lösenordet till ditt Sellavo-konto med en länk via e-post.",
      },
      { property: "og:title", content: "Glömt lösenord — Sellavo" },
      {
        property: "og:description",
        content: "Återställ lösenordet till ditt Sellavo-konto med en länk via e-post.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ForgotPassword,
});

export function ForgotPassword() {
  const { lang } = useLocale();
  const copy = lang === "sv" ? null : getPageCopy(lang).forgot;
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await requestPasswordReset({
        data: { email, redirectTo: `${window.location.origin}/reset-password` },
      });
      setSent(true);
    } catch (err) {
      setError(authErrorText(err));
    } finally {
      setLoading(false);
    }
  };


  return (
    <main className="relative flex min-h-[80vh] items-center justify-center overflow-hidden px-4 py-16">
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-ocean-300 blur-3xl" />
        <div className="absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-ocean-500 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md rounded-lg border border-border bg-card p-8 shadow-xl">
        <Link
          to={localizedPath("/", lang)}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> {lang === "sv" ? "Till startsidan" : getPageCopy(lang).common.backHome}
        </Link>

        {sent ? (
          <div className="mt-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ocean-100 text-ocean-700">
              <MailCheck className="h-6 w-6" />
            </div>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground">
              {copy?.inbox ?? "Kolla din inkorg"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {(copy?.sent ?? "Om {{email}} har ett konto hos oss har vi skickat en länk för att välja ett nytt lösenord. Länken gäller en kort stund — hittar du inte mejlet, titta i skräpposten.").replace("{{email}}", email)}
            </p>
            <Button variant="outline" className="mt-6 w-full" onClick={() => setSent(false)}>
              {copy?.again ?? "Skicka igen"}
            </Button>
          </div>
        ) : (
          <>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground">
              {copy?.title ?? "Glömt lösenordet?"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {copy?.intro ?? "Det händer de bästa. Fyll i din e-postadress så skickar vi en länk där du kan välja ett nytt lösenord."}
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{copy?.email ?? "E-postadress"}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={copy?.placeholder ?? "Ange e-postadress"}
                  autoComplete="username"
                />
              </div>
              {error && (
                <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {copy?.submit ?? "Skicka återställningslänk"}
              </Button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
